import { useState } from "react";
import { X } from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { Button, ModalOverlay, Spinner, ErrorBanner } from "./ui";
import { createContact } from "../services/api";

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  color: colors.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 5,
  fontFamily: fonts.body,
  fontWeight: 600,
};

export function AddContactModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    type: "prospect",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isDirty =
    form.firstName || form.lastName || form.email || form.phone || form.company;

  const handleClose = () => {
    if (isDirty && !window.confirm("Discard unsaved contact?")) return;
    onClose();
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const email = form.email.trim();
    if (!email) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address");
      return;
    }

    setSaving(true);
    try {
      const contact = await createContact({
        email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        tags: [form.type],
      });
      onCreated(contact);
    } catch (err) {
      setError(err.message || "Failed to create contact");
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={handleClose} maxWidth={480}>
        <form
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 20,
                color: colors.text,
                margin: 0,
              }}
            >
              Add Contact
            </h2>
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={handleChange("firstName")}
                placeholder="John"
                style={inputStyle}
                autoFocus
              />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={handleChange("lastName")}
                placeholder="Smith"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Email <span style={{ color: colors.danger }}>*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="john@company.com"
              style={inputStyle}
              required
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="+44 7700 900000"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Company</label>
              <input
                type="text"
                value={form.company}
                onChange={handleChange("company")}
                placeholder="Acme Ltd"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Type</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(CONTACT_TYPES).map(([id, info]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, type: id }))
                  }
                  style={{
                    padding: "5px 10px",
                    borderRadius: 6,
                    border: `1px solid ${form.type === id ? info.color : colors.border}`,
                    background: form.type === id ? info.bg : "transparent",
                    color: form.type === id ? info.color : colors.textSecondary,
                    fontSize: 11,
                    fontFamily: fonts.body,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <ErrorBanner message={error} style={{ marginBottom: 16 }} />
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="default" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              style={{ minWidth: 100 }}
              onClick={saving ? undefined : undefined}
            >
              {saving ? (
                <>
                  <Spinner size={14} color="#fff" />
                  Saving...
                </>
              ) : (
                "Save Contact"
              )}
            </Button>
          </div>
        </form>
    </ModalOverlay>
  );
}
